import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useQuery } from "@tanstack/react-query";
import {
  Sparkles,
  FolderKanban,
  CalendarCheck,
  Receipt,
  ArrowRight,
  ArrowLeft,
  X,
  Play,
} from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { useAuth } from "@/stores/authStore";
import { useOnboardingStore } from "@/stores/onboardingStore";
import { proposeGuide } from "@/api/guides";
import { getYoutubeEmbedUrl } from "@/lib/youtube";
import { cn } from "@/lib/utils";

export const ONBOARDING_WELCOME_VIDEO_STEP = "dashboard_welcome_video_v1";
const DEFAULT_WELCOME_VIDEO_URL = "https://www.youtube.com/watch?v=R8j1gKysgfg";

interface WelcomeVideoModalProps {
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  autoTrigger?: boolean;
}

export const WelcomeVideoModal: React.FC<WelcomeVideoModalProps> = ({
  open: controlledOpen,
  onOpenChange: controlledOnOpenChange,
  autoTrigger = true,
}) => {
  const { t, i18n } = useTranslation();
  const { user } = useAuth();
  const { shouldShowTour, markStepComplete, syncFromUser, fetchOnboardingStatus } =
    useOnboardingStore();

  const isSuperAdmin =
    user?.role === "Super Admin" || user?.role_name === "Super Admin";

  const isRtl = i18n.language === "ar" || document.dir === "rtl";
  const ArrowIcon = isRtl ? ArrowLeft : ArrowRight;

  const [internalOpen, setInternalOpen] = useState(false);
  const [dontShowAgain, setDontShowAgain] = useState(true);

  const isControlled = controlledOpen !== undefined;
  const isOpen = isControlled ? controlledOpen : internalOpen;

  // Sync user's onboarding_steps from auth profile if available
  useEffect(() => {
    if (user?.onboarding_steps && Array.isArray(user.onboarding_steps)) {
      syncFromUser(user.onboarding_steps);
    }
  }, [user?.onboarding_steps, syncFromUser]);

  // Initial fetch of onboarding status on mount
  useEffect(() => {
    if (user && !isSuperAdmin) {
      fetchOnboardingStatus();
    }
  }, [user, isSuperAdmin, fetchOnboardingStatus]);

  // Auto-trigger on initial dashboard visit if step not yet completed
  useEffect(() => {
    if (!autoTrigger || isSuperAdmin || !user) return;

    const timer = setTimeout(() => {
      if (shouldShowTour(ONBOARDING_WELCOME_VIDEO_STEP)) {
        if (controlledOnOpenChange) {
          controlledOnOpenChange(true);
        } else {
          setInternalOpen(true);
        }
      }
    }, 800);

    return () => clearTimeout(timer);
  }, [autoTrigger, isSuperAdmin, user, shouldShowTour, controlledOnOpenChange]);

  // Fetch configured guide from backend (general_overview or home_dashboard)
  const { data: guideResponse } = useQuery({
    queryKey: ["guide-welcome-video"],
    queryFn: () => proposeGuide("general_overview"),
    staleTime: 30 * 60 * 1000,
    refetchOnWindowFocus: false,
    retry: 1,
  });

  const activeVideoUrl =
    guideResponse?.guide?.link?.trim() || DEFAULT_WELCOME_VIDEO_URL;
  const embedBase = getYoutubeEmbedUrl(activeVideoUrl);
  // Add rel=0 and autoplay=1 when opened for smooth playback
  const embedUrl = embedBase ? `${embedBase}?rel=0&autoplay=1` : null;

  const handleClose = (markAsDone: boolean = true) => {
    if (markAsDone && dontShowAgain) {
      markStepComplete(ONBOARDING_WELCOME_VIDEO_STEP);
    }

    if (isControlled && controlledOnOpenChange) {
      controlledOnOpenChange(false);
    } else {
      setInternalOpen(false);
    }
  };

  const handleComplete = () => {
    markStepComplete(ONBOARDING_WELCOME_VIDEO_STEP);
    handleClose(false);
  };

  const handleOpenChange = (nextOpen: boolean) => {
    if (!nextOpen) {
      handleClose(true);
    } else {
      if (isControlled && controlledOnOpenChange) {
        controlledOnOpenChange(true);
      } else {
        setInternalOpen(true);
      }
    }
  };

  if (isSuperAdmin) return null;

  return (
    <Dialog open={isOpen} onOpenChange={handleOpenChange}>
      <DialogContent
        className={cn(
          "max-w-4xl w-[95vw] sm:w-[90vw] p-0 overflow-hidden",
          "border border-border/60 bg-background/95 backdrop-blur-xl shadow-2xl rounded-2xl",
          "[&>button]:hidden" // Custom header close button
        )}
        onPointerDownOutside={(e) => e.preventDefault()}
      >
        {/* Top glowing gradient brand bar */}
        <div className="h-1.5 w-full bg-gradient-to-r from-amber-500 via-amber-400 to-amber-600 dark:from-amber-600 dark:via-amber-400 dark:to-yellow-500" />

        <div className="p-5 sm:p-7 space-y-5">
          {/* Header section */}
          <div className="relative flex items-start justify-between gap-4">
            <div className="space-y-1.5 pe-8">
              <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold bg-amber-500/10 text-amber-600 dark:text-amber-400 border border-amber-500/20">
                <Sparkles className="h-3.5 w-3.5 animate-pulse" />
                <span>
                  {t(
                    "onboarding.welcome_video.badge",
                    "🚀 جولة سريعة • 90 ثانية لاكتشاف المنصة"
                  )}
                </span>
              </div>

              <DialogHeader className="text-start">
                <DialogTitle className="text-2xl sm:text-3xl font-bold tracking-tight text-foreground">
                  {t("onboarding.welcome_video.title", "مرحباً بك في AvocatPro!")}
                </DialogTitle>
                <DialogDescription className="text-sm sm:text-base text-muted-foreground leading-relaxed pt-1">
                  {t(
                    "onboarding.welcome_video.subtitle",
                    "دليل تعريفي سريع مدته 90 ثانية يشرح لك كيفية تسيير مكتبك وإدارة ملفاتك ومواعيدك وفواتيرك بكل احترافية."
                  )}
                </DialogDescription>
              </DialogHeader>
            </div>

            {/* Custom Close Button */}
            <button
              onClick={() => handleClose(true)}
              className={cn(
                "rounded-full p-2 text-muted-foreground hover:text-foreground",
                "hover:bg-muted/80 transition-colors focus:outline-none focus:ring-2 focus:ring-amber-500/50"
              )}
              aria-label={t("common.close", "إغلاق")}
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          {/* Video Player Box */}
          <div className="relative aspect-video w-full rounded-xl overflow-hidden bg-black/95 shadow-xl border border-border/40 group">
            {embedUrl ? (
              <iframe
                className="w-full h-full border-0"
                src={embedUrl}
                title={guideResponse?.guide?.title || "AvocatPro Guide"}
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              />
            ) : (
              <div className="flex flex-col items-center justify-center h-full p-6 text-center text-muted-foreground gap-3">
                <Play className="h-10 w-10 text-muted-foreground/50" />
                <p className="text-sm max-w-md">
                  {t(
                    "onboarding.welcome_video.video_load_error",
                    "تعذر تحميل الفيديو، يرجى التحقق من اتصال الإنترنت أو المحاولة لاحقاً."
                  )}
                </p>
              </div>
            )}
          </div>

          {/* Feature Highlights (3 Cards) */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div className="flex items-start gap-3 p-3.5 rounded-xl bg-muted/40 border border-border/40 hover:bg-muted/60 transition-colors">
              <div className="p-2 rounded-lg bg-blue-500/10 text-blue-600 dark:text-blue-400 shrink-0 mt-0.5">
                <FolderKanban className="h-4 w-4" />
              </div>
              <div className="space-y-0.5 text-start">
                <h4 className="text-xs font-semibold text-foreground">
                  {t(
                    "onboarding.welcome_video.feature_cases_title",
                    "إدارة القضايا والملفات"
                  )}
                </h4>
                <p className="text-[11px] text-muted-foreground leading-tight">
                  {t(
                    "onboarding.welcome_video.feature_cases_desc",
                    "تتبع ملفات الموكلين، الجلسات، والمستندات بضغطة زر."
                  )}
                </p>
              </div>
            </div>

            <div className="flex items-start gap-3 p-3.5 rounded-xl bg-muted/40 border border-border/40 hover:bg-muted/60 transition-colors">
              <div className="p-2 rounded-lg bg-purple-500/10 text-purple-600 dark:text-purple-400 shrink-0 mt-0.5">
                <CalendarCheck className="h-4 w-4" />
              </div>
              <div className="space-y-0.5 text-start">
                <h4 className="text-xs font-semibold text-foreground">
                  {t(
                    "onboarding.welcome_video.feature_agenda_title",
                    "الأجندة والمواعيد"
                  )}
                </h4>
                <p className="text-[11px] text-muted-foreground leading-tight">
                  {t(
                    "onboarding.welcome_video.feature_agenda_desc",
                    "تنظيم مواعيد الاستشارات والجلسات وتجنب أي تعارض في التواريخ."
                  )}
                </p>
              </div>
            </div>

            <div className="flex items-start gap-3 p-3.5 rounded-xl bg-muted/40 border border-border/40 hover:bg-muted/60 transition-colors">
              <div className="p-2 rounded-lg bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 shrink-0 mt-0.5">
                <Receipt className="h-4 w-4" />
              </div>
              <div className="space-y-0.5 text-start">
                <h4 className="text-xs font-semibold text-foreground">
                  {t(
                    "onboarding.welcome_video.feature_billing_title",
                    "الفواتير والمصاريف"
                  )}
                </h4>
                <p className="text-[11px] text-muted-foreground leading-tight">
                  {t(
                    "onboarding.welcome_video.feature_billing_desc",
                    "إصدار فواتير الأتعاب، تتبع المدفوعات ومراقبة مصاريف المكتب بسهولة."
                  )}
                </p>
              </div>
            </div>
          </div>

          {/* Footer with actions and "Don't show again" */}
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4 pt-2 border-t border-border/50">
            <label className="flex items-center gap-2.5 text-xs text-muted-foreground cursor-pointer select-none">
              <Checkbox
                checked={dontShowAgain}
                onCheckedChange={(checked) => setDontShowAgain(!!checked)}
              />
              <span>
                {t(
                  "onboarding.welcome_video.dont_show_again",
                  "عدم إظهار هذا الفيديو تلقائياً مرة أخرى"
                )}
              </span>
            </label>

            <div className="flex items-center gap-2.5 w-full sm:w-auto">
              <Button
                variant="outline"
                size="sm"
                onClick={() => handleClose(true)}
                className="flex-1 sm:flex-none text-xs sm:text-sm font-medium"
              >
                {t("onboarding.welcome_video.cta_skip", "تخطي الآن")}
              </Button>

              <Button
                variant="gold"
                size="sm"
                onClick={handleComplete}
                className={cn(
                  "flex-1 sm:flex-none text-xs sm:text-sm font-semibold gap-2 shadow-md hover:shadow-lg",
                  "transition-all duration-200"
                )}
              >
                <span>
                  {t(
                    "onboarding.welcome_video.cta_start",
                    "ابدأ استخدام المنصة"
                  )}
                </span>
                <ArrowIcon className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
